import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'primereact/toolbar';
import { ListBox } from 'primereact/listbox';
import { Dropdown } from 'primereact/dropdown';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import '../css/global.css';
import DBService from '../service/DBService';

function TalentcheckModule({ status, module, onSave }) {
    const [participants, setParticipants] = useState([]);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [tempScores, setTempScores] = useState({});
    const [autoSaveState, setAutoSaveState] = useState('idle'); // idle | pending | saving | saved | error
    const toast = React.useRef(null);
    const saveTimeoutRef = React.useRef(null);
    const pendingSaveRef = React.useRef(null);
    const pendingParticipantRef = React.useRef(null);
    const isMountedRef = React.useRef(true);
    const retryCountRef = React.useRef(0);
    const retryTimeoutRef = React.useRef(null);
    const AUTO_SAVE_DELAY = 800;
    const MAX_SAVE_RETRIES = 5;
    const BASE_BACKOFF_MS = 1000; // 1s base delay for exponential backoff

    const currentDate = sessionStorage.getItem('date');

    const dropdownOptions = [
        { label: "k.A.", value: "k.A." },
        { label: "1", value: 1 },
        { label: "2", value: 2 },
        { label: "3", value: 3 },
        { label: "4", value: 4 },
        { label: "5", value: 5 }
    ];

    const REQUIRED_FIELDS = [
        'kommunikationsfaehigkeit',
        'planungsfaehigkeit',
        'problemloesefaehigkeit',
        'ordentlichkeit',
        'selbststaendigkeit',
        'arbeitstempo',
        'selbsteinschaetzung',
        'selfAssessment',
        'teamfaehigkeit',
        'kritikfaehigkeit',
        'praesentationsfaehigkeit',
        'durchhaltevermoegen',
        'arbeitsgenauigkeit',
        'verantwortungsfaehigkeit',
    ];

    const SELF_ASSESSMENT_FIELDS = new Set(['selbsteinschaetzung', 'selfAssessment']);
    const INVALID_FIELD_STRINGS = new Set(['k.a.', 'k.a', 'ka', 'n/a', 'na', '0', '0.0']);

    /**
     * Parse a number from a string that may use a comma as decimal separator (e.g., "3,5").
     * This is a minimal helper for de-DE style inputs in text fields. It intentionally does not
     * handle thousands separators or complex locale formats; it just normalizes a single
     * comma to a dot so that parseFloat can do its job.
     *
     * @param {string} s
     * @returns {number} a JavaScript number or NaN if parsing fails
     */
    const parseLocaleNumber = (s) => {
        if (typeof s !== 'string') return NaN;
        return parseFloat(s.replace(',', '.'));
    };

    const pinsAreEqual = (pinA, pinB) => {
        if (pinA === pinB) {
            return true;
        }
        if (pinA === null || pinA === undefined || pinB === null || pinB === undefined) {
            return false;
        }
        return String(pinA) === String(pinB);
    };

    const getEntryValue = (entry, key) => {
        if (!entry || !key) {
            return undefined;
        }

        if (Object.prototype.hasOwnProperty.call(entry, key)) {
            return entry[key];
        }

        const lowerKey = key.toLowerCase();
        const matchedKey = Object.keys(entry).find(
            (currentKey) => currentKey.toLowerCase() === lowerKey
        );

        return matchedKey ? entry[matchedKey] : undefined;
    };

    /**
     * Validates whether a given field value should count as "filled".
     * Note: For string inputs containing decimals, we accept a comma as the decimal separator
     * via parseLocaleNumber (e.g., "2,5" -> 2.5). This mirrors common de-DE input behavior.
     */
    const isValidFieldValue = (value) => {
        if (value === null || value === undefined) {
            return false;
        }

        if (typeof value === 'number') {
            return value > 0;
        }

        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'string') {
            const trimmed = value.trim();
            if (!trimmed) {
                return false;
            }

            const normalized = trimmed.toLowerCase();
            if (INVALID_FIELD_STRINGS.has(normalized)) {
                return false;
            }

            // Use helper to support comma decimal separator (see parseLocaleNumber)
            const normalizedNumeric = parseLocaleNumber(normalized);
            if (!Number.isNaN(normalizedNumeric)) {
                return normalizedNumeric > 0;
            }

            return true;
        }

        return true;
    };

    /**
     * Minimal "filled" check for PIN coloring:
     * Only null, undefined, and empty string count as empty.
     */
    const isFieldFilled = (value) => {
        if (value === null || value === undefined) {
            return false;
        }
        if (typeof value === 'string') {
            return value === '' ? false : true;
        }
        return true;
    };

    const hasEvaluatorInput = (entry) => {
        if (!entry) {
            return false;
        }

        const excludedKeys = new Set([
            'id',
            'pin',
            'datum',
            'date',
            'status',
            'role',
            'module',
            'zugangscode',
            'timestamp',
            'createdat',
            'updatedat'
        ]);

        return Object.entries(entry).some(([rawKey, rawValue]) => {
            if (!rawKey) {
                return false;
            }

            const key = rawKey.toString().toLowerCase();
            if (excludedKeys.has(key)) {
                return false;
            }

            return isValidFieldValue(rawValue);
        });
    };

    const getRelevantFieldValues = (entry) => {
        if (!entry) {
            return [];
        }

        return REQUIRED_FIELDS
            .filter((field) => !SELF_ASSESSMENT_FIELDS.has(field))
            .map((field) => ({ field, value: getEntryValue(entry, field) }));
    };

    const isParticipantComplete = (entry) => {
        const relevantFields = getRelevantFieldValues(entry);
        if (relevantFields.length === 0) {
            return false;
        }

        return relevantFields.every(({ value }) => isValidFieldValue(value));
    };

    const getPinColor = (entry) => {
        const relevantFields = getRelevantFieldValues(entry);
        if (relevantFields.length === 0) {
            return 'red';
        }

        const filledCount = relevantFields.filter(({ value }) => isFieldFilled(value)).length;

        if (filledCount === 0) {
            return 'red';
        }

        if (filledCount === relevantFields.length) {
            return 'green';
        }

        return 'orange';
    };

    const deriveParticipantMeta = (entry) => ({
        isMissingEvaluation: !hasEvaluatorInput(entry),
        isComplete: isParticipantComplete(entry),
        pinColor: getPinColor(entry),
    });

    const createParticipantOption = (entry) => {
        if (!entry) {
            return null;
        }

        const pinField = getEntryValue(entry, 'pin') ?? null;
        const participantValue = pinField ?? null;

        if (participantValue === null || participantValue === undefined) {
            return null;
        }

        const { isMissingEvaluation, isComplete, pinColor } = deriveParticipantMeta(entry);

        return {
            label: String(participantValue),
            value: participantValue,
            isMissingEvaluation,
            isComplete,
            pinColor,
        };
    };

    const compareParticipants = (a, b) => {
        const aNumeric = Number(a.value);
        const bNumeric = Number(b.value);
        const aIsNumeric = !Number.isNaN(aNumeric);
        const bIsNumeric = !Number.isNaN(bNumeric);

        if (aIsNumeric && bIsNumeric) {
            return aNumeric - bNumeric;
        }

        return String(a.value).localeCompare(String(b.value), undefined, { numeric: true, sensitivity: 'base' });
    };

    const updateParticipantState = (pin, data) => {
        if (pin === null || pin === undefined) {
            return;
        }

        const { isMissingEvaluation, isComplete, pinColor } = deriveParticipantMeta(data);
        setParticipants((prev) =>
            prev.map((participant) =>
                pinsAreEqual(participant.value, pin)
                    ? { ...participant, isMissingEvaluation, isComplete, pinColor }
                    : participant
            )
        );
    };

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
            pendingSaveRef.current = null;
            pendingParticipantRef.current = null;
        };
    }, []);

    const getCollectionName = (module) => {
        const role = sessionStorage.getItem('role');
        if (!role) {
            console.error('Role ist nicht im sessionStorage gesetzt.');
            return null;
        }

        let roleMapped;
        if (role.toLowerCase() === 'boz') {
            roleMapped = 'BOZ';
        } else if (role.toLowerCase() === 'lehrkraft') {
            roleMapped = 'LUL';
        } else {
            console.error(`Ungültige Rolle: ${role}`);
            return null;
        }

        const moduleMapping = {
            Organisation: 'Orgamodul',
            Praxismodul1: 'Praxismodul1',
            Praxismodul2: 'Praxismodul2',
            Sozialmodul: 'Sozialmodul',
        };

        const mappedModule = moduleMapping[module];
        if (!mappedModule) {
            console.error(`Ungültiges Modul: ${module}`);
            return null;
        }

        return `${mappedModule}_${roleMapped}`;
    };

    useEffect(() => {
        const fetchParticipants = async () => {
            try {
                const collectionName = getCollectionName(module);
                if (!collectionName) {
                    console.warn('Collection-Name konnte nicht generiert werden.');
                    return;
                }

                const result = await DBService.fetchDataByDate(collectionName, currentDate);
                console.log(`Abgerufene Daten aus der Collection "${collectionName}" mit Datum "${currentDate}":`, result);

                if (!result || result.length === 0) {
                    console.warn(`Keine Daten in der Collection "${collectionName}" für das Datum "${currentDate}" gefunden.`);
                    setParticipants([]);
                    return;
                }

                const participantsWithStatus = result
                    .map((entry) => createParticipantOption(entry))
                    .filter((participantOption) => participantOption !== null)
                    .sort(compareParticipants);

                setParticipants(participantsWithStatus);
                console.log('Teilnehmerliste:', result);
            } catch (error) {
                console.error('Fehler beim Abrufen der Teilnehmer:', error);
            }
        };

        fetchParticipants();
    }, [module, currentDate]);

    const handleParticipantChange = async (value) => {
        try {
            if (value === selectedParticipant) {
                return;
            }

            await flushAutoSave();
            console.log(`Teilnehmer geändert: ${value}`);
            const collectionName = getCollectionName(module);
            if (!collectionName) return;

            console.log(`Lade Daten für die Collection: ${collectionName} mit Pin: ${value}`);
            const filters = { field: "pin", value: value };
            const result = await DBService.fetchData(collectionName, filters);

            console.log(`Ergebnis für Pin ${value}:`, result);

            if (result.length === 0) {
                console.warn(`Keine Daten für die Pin "${value}" gefunden.`);
                setTempScores({});
                setSelectedParticipant(value);
                setAutoSaveState('idle');
                updateParticipantState(value, {});
                return;
            }

            const selectedData = result[0];
            console.log('Ausgewählte Daten:', selectedData);
            setSelectedParticipant(value);
            setTempScores(selectedData);
            setAutoSaveState('idle');
            updateParticipantState(value, selectedData);
        } catch (error) {
            console.error('Fehler beim Laden der Daten des Teilnehmers:', error);
        }
    };

    const scheduleAutoSave = (updatedScores) => {
        pendingSaveRef.current = updatedScores;
        pendingParticipantRef.current = selectedParticipant;
        // reset retry state for a fresh save cycle
        retryCountRef.current = 0;
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        setAutoSaveState('pending');

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(async () => {
            saveTimeoutRef.current = null;
            const dataToPersist = pendingSaveRef.current;
            const participantPin = pendingParticipantRef.current;
            pendingSaveRef.current = null;
            pendingParticipantRef.current = null;
            if (!dataToPersist) {
                return;
            }
            if (participantPin === null || participantPin === undefined) {
                return;
            }
            try {
                await performAutoSave(dataToPersist, participantPin);
            } catch (error) {
                console.error('Fehler beim automatischen Speichern:', error);
            }
        }, AUTO_SAVE_DELAY);
    };

    const flushAutoSave = async () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
            saveTimeoutRef.current = null;
        }
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }

        if (!pendingSaveRef.current) {
            return;
        }

        const dataToPersist = pendingSaveRef.current;
        const participantPin = pendingParticipantRef.current;
        pendingSaveRef.current = null;
        pendingParticipantRef.current = null;
        if (participantPin === null || participantPin === undefined) {
            return;
        }
        await performAutoSave(dataToPersist, participantPin);
    };

    const performAutoSave = async (dataToPersist, participantPin) => {
        if (participantPin === null || participantPin === undefined || !dataToPersist) {
            return;
        }

        const collectionName = getCollectionName(module);
        if (!collectionName) return;

        if (isMountedRef.current) {
            setAutoSaveState('saving');
        }

        try {
            await DBService.updateData(collectionName, participantPin, dataToPersist);
            if (onSave) {
                onSave(dataToPersist);
            }

            updateParticipantState(participantPin, dataToPersist);

            // success: clear any retry state
            retryCountRef.current = 0;
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            if (isMountedRef.current) {
                setAutoSaveState('saved');
                setTimeout(() => {
                    if (isMountedRef.current) {
                        setAutoSaveState((prev) => (prev === 'saved' ? 'idle' : prev));
                    }
                }, 2000);
            }
        } catch (error) {
            console.error('Fehler beim automatischen Speichern:', error);

            // keep the latest data & pin for retry attempts
            pendingSaveRef.current = dataToPersist;
            pendingParticipantRef.current = participantPin;

            // if we can still retry, schedule with exponential backoff
            const attempt = retryCountRef.current + 1;
            if (attempt <= MAX_SAVE_RETRIES) {
                retryCountRef.current = attempt;
                const delay = BASE_BACKOFF_MS * Math.pow(2, attempt - 1); // 1s, 2s, 4s, 8s, 16s

                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                }
                if (isMountedRef.current) {
                    setAutoSaveState('pending');
                }
                retryTimeoutRef.current = setTimeout(() => {
                    if (!isMountedRef.current) return;
                    // Re-attempt with the same payload
                    performAutoSave(pendingSaveRef.current, pendingParticipantRef.current);
                }, delay);

                // Optional: lightweight feedback without spamming errors
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Speichern fehlgeschlagen, erneuter Versuch',
                    detail: `Versuch ${attempt} von ${MAX_SAVE_RETRIES} in ${Math.round(delay / 1000)}s.`,
                    life: 3000,
                });
            } else {
                // give up: stop retrying and surface an error state
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                    retryTimeoutRef.current = null;
                }
                pendingSaveRef.current = null;
                pendingParticipantRef.current = null;
                retryCountRef.current = 0;
                if (isMountedRef.current) {
                    setAutoSaveState('error');
                }
                toast.current?.show({
                    severity: 'error',
                    summary: 'Speicherfehler',
                    detail: 'Änderungen konnten nach mehreren Versuchen nicht gespeichert werden.',
                    life: 4000,
                });
            }
        }
    };

    const handleInputChange = (event, field) => {
        const value = event?.value ?? event?.target?.value ?? event;
        const updatedScores = { ...tempScores, [field]: value };
        setTempScores(updatedScores);

        if (!selectedParticipant) {
            return;
        }
        scheduleAutoSave(updatedScores);
    };

    const renderContent = () => {
        if (!selectedParticipant) {
            return (
                <div className="p-grid p-fluid" style={{ rowGap: '1rem' }}>
                    {[...Array(6)].map((_, index) => (
                        <div className="p-field" key={index}>
                            <Skeleton width="100%" height="2rem" />
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="p-grid p-fluid" style={{ rowGap: '1rem' }}>
                <div className="p-col-6">
                    <div className="p-field">
                        <label>Kommunikationsfähigkeit</label>
                        <Dropdown
                            value={tempScores.kommunikationsfaehigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'kommunikationsfaehigkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Planungsfähigkeit</label>
                        <Dropdown
                            value={tempScores.planungsfaehigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'planungsfaehigkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Problemlösefähigkeit</label>
                        <Dropdown
                            value={tempScores.problemloesefaehigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'problemloesefaehigkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Ordentlichkeit</label>
                        <Dropdown
                            value={tempScores.ordentlichkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'ordentlichkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Selbstständigkeit</label>
                        <Dropdown
                            value={tempScores.selbststaendigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'selbststaendigkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Arbeitstempo</label>
                        <Dropdown
                            value={tempScores.arbeitstempo || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'arbeitstempo')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Selbsteinschätzung</label>
                        <InputText
                            value={tempScores.selbsteinschaetzung || ''}
                            onChange={(e) => handleInputChange(e, 'selbsteinschaetzung')}
                        />
                    </div>
                </div>
                <div className="p-col-6">
                    <div className="p-field">
                        <label>Teamfähigkeit</label>
                        <Dropdown
                            value={tempScores.teamfaehigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'teamfaehigkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Kritikfähigkeit</label>
                        <Dropdown
                            value={tempScores.kritikfaehigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'kritikfaehigkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Präsentationsfähigkeit</label>
                        <Dropdown
                            value={tempScores.praesentationsfaehigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'praesentationsfaehigkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Durchhaltevermögen</label>
                        <Dropdown
                            value={tempScores.durchhaltevermoegen || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'durchhaltevermoegen')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Arbeitsgenauigkeit</label>
                        <Dropdown
                            value={tempScores.arbeitsgenauigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'arbeitsgenauigkeit')}
                        />
                    </div>
                    <div className="p-field">
                        <label>Verantwortungsfähigkeit</label>
                        <Dropdown
                            value={tempScores.verantwortungsfaehigkeit || ''}
                            options={dropdownOptions}
                            onChange={(e) => handleInputChange(e, 'verantwortungsfaehigkeit')}
                        />
                    </div>

                </div>
            </div>
        );
    };

    const leftContents = (
        <h1>{`${sessionStorage.getItem('role')?.toLowerCase() === 'boz' ? 'BOZ' : 'Lehrkraft'
            } - ${module} - [${currentDate}]`}</h1>
    );

    const rightContents = (
        <span
            className={`autosave-indicator ${autoSaveState === 'saving'
                ? 'autosave-saving'
                : autoSaveState === 'saved'
                    ? 'autosave-saved'
                    : autoSaveState === 'error'
                        ? 'autosave-error'
                        : autoSaveState === 'pending'
                            ? 'autosave-pending'
                            : ''
                }`}
        >
            {autoSaveState === 'saving' && 'Speichern...'}
            {autoSaveState === 'saved' && 'Alle Änderungen gespeichert'}
            {autoSaveState === 'pending' && 'Änderungen werden gespeichert...'}
            {autoSaveState === 'error' && 'Speichern fehlgeschlagen'}
            {autoSaveState === 'idle' && 'Automatisches Speichern aktiv'}
        </span>
    );

    const renderParticipantOption = (option) => {
        const classes = ['pin-option'];
        if (option.pinColor) {
            classes.push(`pin-color-${option.pinColor}`);
        }
        if (option.isMissingEvaluation) {
            classes.push('participant-missing');
        }

        return <div className={classes.join(' ')}>{option.label}</div>;
    };

    return (
        <div>
            <Toast ref={toast} />
            <Toolbar className="toolbar" start={leftContents} end={rightContents} />
            <div className="p-grid">
                <div className="p-col-3">
                    <Card title="Teilnehmer" className="p-shadow-3">
                        <ListBox
                            filter
                            value={selectedParticipant}
                            options={participants}
                            onChange={(e) => handleParticipantChange(e.value)}
                            listStyle={{ height: '600px' }}
                            style={{ width: '100%' }}
                            itemTemplate={renderParticipantOption}
                        />
                    </Card>
                </div>
                <div className="p-col-9">
                    <Card title={`Daten für Teilnehmer [${selectedParticipant}]`} className="p-shadow-3">
                        {renderContent()}
                    </Card>
                </div>
            </div>
        </div>
    );
}

export default TalentcheckModule;
